import {
  gold,
  gray7,
  gray10,
  gray90,
  gray95,
  kleinBlue,
  white,
  black,
  black90,
  fuchsia,
  gray60,
  gray75,
  black70,
} from '@spotify-internal/encore-foundation';
import { colorThemeGenerator } from '@spotify-internal/encore-web';

const darkComponents = {
  app: 'dark',
  table: 'dark',
  tableRow: 'dark',
  buttonIcon: 'dark',
  loadingIndicator: 'dark',
  navBar: 'dark',
  navBarList: 'dark',
  navBarListItem: 'dark',
  dialogAlert: 'dark',
  dialogConfirmation: 'dark',
  formGroup: 'dark',
  // formInput: 'dark',
  button: 'dark',
};

const help = {
  link: {
    text: kleinBlue,
    divider: gray75,
  },
  grayBox: {
    background: gray90,
    text: black,
  },
  banner: {
    background: kleinBlue,
    text: white,
    ctaText: black,
  },
  sideMenu: {
    currentCategoryIndicator: fuchsia,
    unselectedCategory: gray60,
    selectedCategory: black,
  },
};

const searchBar = {
  background: white,
  border: gray60,
  suggestionHeader: black70,
  suggestionSelectedBackground: gray90,
  icon: gray60,
};

// dark theme is the default
// everything required should be specified here
export const defaultTheme = {
  ...colorThemeGenerator({ primaryColor: kleinBlue }),
  ...darkComponents,
  app: 'dark',
  loadingIndicator: 'dark',
  box: {
    background: gray10,
    text: white,
    tileOneBackground: gray10,
    tileTwoBackground: black,
  },
  cta: {
    background: white,
    text: gray7,
  },
  label: {
    text: gray10,
  },
  layout: {
    background: black90,
    text: white,
  },
  nav: {
    background: black,
    text: white,
    sideMenuBackground: black,
    sideMenuTextColor: white,
  },
  link: {
    color: white,
  },
  help,
  searchBar,
};

export type Theme = typeof defaultTheme & {
  help: {
    link: {
      text: string;
      divider: string;
    };
    grayBox: {
      background: string;
      text: string;
    };
    sideMenu: {
      currentCategoryIndicator: string;
      unselectedCategory: string;
      selectedCategory: string;
    };
  };
};

export const darkTheme = defaultTheme;

/* light theme only applies to the current page area, not the navigation, etc */
export const lightTheme: Theme = {
  ...defaultTheme,
  loadingIndicator: 'light',
  cta: {
    ...defaultTheme.cta,
    background: black,
    text: white,
  },
  box: {
    ...defaultTheme.box,
    background: white,
    text: black,
  },
  link: {
    ...defaultTheme.link,
    color: black,
  },
};

export const TEMPLATE_THEMES: { [name: string]: Theme } = {
  Dark: darkTheme,
  Light: lightTheme,
};

// module themes get merged with the template theme
export const MODULE_THEMES: { [name: string]: Partial<Theme> } = {
  whiteOnBlue: {
    box: {
      ...defaultTheme.box,
      background: kleinBlue,
      text: white,
    },
    cta: {
      ...defaultTheme.cta,
      background: white,
      text: kleinBlue,
    },
  },
  blueOnWhite: {
    box: {
      ...defaultTheme.box,
      background: white,
      text: kleinBlue,
    },
    cta: {
      ...defaultTheme.cta,
      background: kleinBlue,
      text: white,
    },
  },
  blueOnBlack: {
    box: {
      ...defaultTheme.box,
      background: gray10,
      text: kleinBlue,
    },
    cta: {
      ...defaultTheme.cta,
      background: kleinBlue,
      text: gray10,
    },
  },
  blackOnWhite: {
    box: {
      ...defaultTheme.box,
      background: white,
      text: gray10,
      tileOneBackground: gray95,
      tileTwoBackground: gray90,
    },
    cta: {
      ...defaultTheme.cta,
      background: gray10,
      text: gray90,
    },
    link: {
      ...defaultTheme.link,
      color: gray10,
    },
  },
  blackOnGold: {
    box: {
      ...defaultTheme.box,
      background: gold,
      text: gray10,
    },
    cta: {
      ...defaultTheme.cta,
      background: gray10,
      text: gold,
    },
  },
  goldOnBlack: {
    box: {
      ...defaultTheme.box,
      background: gray10,
      text: gold,
    },
    cta: {
      ...defaultTheme.cta,
      background: gold,
      text: gray10,
    },
  },
  whiteOnBlack: {
    box: {
      ...defaultTheme.box,
      background: gray10,
      text: white,
      tileOneBackground: gray10,
      tileTwoBackground: black,
    },
    cta: {
      ...defaultTheme.cta,
      background: white,
      text: gray7,
    },
    link: {
      ...defaultTheme.link,
      color: white,
    },
  },
  blackOnGray: {
    box: {
      ...defaultTheme.box,
      background: gray90,
      text: gray10,
    },
    cta: {
      ...defaultTheme.cta,
      background: white,
      text: gray7,
    },
  },
};
